/**
 * RAE — Appointment Engine module entry.
 *
 * Default export renders the RAE route tree so App.tsx's
 *   <Route path="/rae/*" element={<RAE />} />
 * continues to work unchanged.
 *
 * Two surfaces:
 *   - Officer/Admin → AppointmentsLanding (unified Today/Upcoming/Bookings/
 *     Manage/Config tabs over M365 Calendar + MS Bookings + Teams Meetings).
 *   - Customer/Kiosk → 5-step BookingWizard (the public booking experience).
 *
 * The previous stub FsrCalendarView is now folded into AppointmentsLanding's
 * Today tab — there is no separate "my-calendar" route. Book-on-behalf still
 * wraps the public wizard so the FSR sees the same screens the customer sees.
 */

import { Routes, Route, Navigate, Link } from 'react-router';
import { SplatRedirect } from '@/components/common/SplatRedirect';
import { Pill, Button } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { BookingWizard } from './booking/BookingWizard';
import { BookOnBehalf } from './BookOnBehalf';
import { AppointmentsLanding } from './AppointmentsLanding';
import './rae.css';

/** Home page at `/rae` — branches by role. */
export function RaeHome(): JSX.Element {
  const { user } = useAuth();
  // Wave 1 G-7 — Customer is no longer a UserRole; Kiosk is now an Azure
  // SPA, not a Teams user. Every authenticated ROP-Teams user is an
  // employee, so the officer-vs-customer branch collapses. The non-officer
  // booking-wizard surface lives in the customer portal app (RCP), not here.
  // Keeping the JSX below as a non-hit fallback; if it ever renders that's
  // a state bug worth surfacing (no auth, no role).
  const isOfficer = user != null;

  if (isOfficer) {
    return <AppointmentsLanding />;
  }

  // Unauth fallback — should not happen in normal Teams SSO flow.
  return (
    <div className="module-page">
      <header className="module-page__header">
        <div className="rae-customer-hero">
          <h1>Book an appointment</h1>
          <Pill variant="muted">Module RAE</Pill>
        </div>
      </header>

      <div className="rae-customer-cta">
        <h2>Ready to book?</h2>
        <p>
          Start a new appointment — we'll match you with one of our employee-owners.
        </p>
        <Link to="book">
          <Button variant="primary" size="lg">Book appointment →</Button>
        </Link>
      </div>
    </div>
  );
}

/** Default export — full RAE route tree. */
export default function RAE(): JSX.Element {
  return (
    <Routes>
      <Route index element={<RaeHome />} />
      <Route path="book" element={<BookingWizard />} />
      <Route path="book-on-behalf" element={<BookOnBehalf />} />
      {/* Legacy `/my-calendar` redirects to the unified landing — Today tab is now the default. */}
      <Route path="my-calendar" element={<Navigate to="." replace />} />
      <Route path="*" element={<SplatRedirect />} />
    </Routes>
  );
}
